import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { KeyRound, Smartphone, Globe, Link2, RefreshCw, Loader2, ShieldAlert, Archive, Trash2, LayoutGrid, List, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/utils';
import IdentityMarketplace from '@/components/IdentityMarketplace';

const PLATFORM_ICONS = {
  google: Globe,
  reddit: Smartphone,
  reddit_via_google: Link2,
};

const STATUS_COLORS = {
  fresh: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  in_use: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  banned: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  archived: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',
};

function parseMailboxLines(text) {
  const rows = [];
  for (const raw of (text || '').split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    let email = '';
    let password = '';
    if (line.includes(':')) {
      const idx = line.indexOf(':');
      email = line.slice(0, idx).trim();
      password = line.slice(idx + 1).trim();
    } else {
      const parts = line.split(/\s+/);
      email = parts[0] || '';
      password = parts.slice(1).join(' ');
    }
    if (email && password && email.includes('@')) rows.push({ email, password });
  }
  return rows;
}

export default function Identities() {
  const qc = useQueryClient();
  const [platformFilter, setPlatformFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [details, setDetails] = React.useState(null);
  const [batchCount, setBatchCount] = React.useState(1);
  const [mailboxText, setMailboxText] = React.useState('');
  const [preferMobile, setPreferMobile] = React.useState(true);
  const [gmailCredentialId, setGmailCredentialId] = React.useState('');
  const [aliasMode, setAliasMode] = React.useState('plus_dot');
  const [view, setView] = React.useState(() => {
    if (typeof window === 'undefined') return 'list';
    return localStorage.getItem('identities.view') || 'list';
  });
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('identities.view', view);
    }
  }, [view]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['identities', platformFilter, statusFilter],
    queryFn: () => {
      const params = {};
      if (platformFilter !== 'all') params.platform = platformFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      return api.get('/identities', { params }).then(r => r.data);
    },
    refetchInterval: 15000,
  });


  const { data: capacity, refetch: refetchCapacity } = useQuery({
    queryKey: ['identities', 'reddit-standalone-capacity'],
    queryFn: () => api.get('/identities/reddit-standalone/capacity').then(r => r.data),
    enabled: platformFilter === 'reddit' && view === 'list',
    refetchInterval: 20000,
  });

  const { data: gmailStatus, refetch: refetchGmail } = useQuery({
    queryKey: ['identities', 'gmail-oauth-status'],
    queryFn: () => api.get('/identities/gmail-oauth/status').then(r => r.data),
    enabled: platformFilter === 'reddit' && view === 'list',
    refetchInterval: 30000,
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('gmail_oauth');
    if (!flag) return;
    if (flag === 'connected') {
      toast.success(`Gmail connected${params.get('email') ? `: ${params.get('email')}` : ''}`);
      refetchGmail();
      refetchCapacity();
    } else if (flag === 'error') {
      toast.error(`Gmail OAuth failed${params.get('reason') ? `: ${params.get('reason')}` : ''}`);
    }
    params.delete('gmail_oauth');
    params.delete('email');
    params.delete('id');
    params.delete('reason');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, [refetchGmail, refetchCapacity]);

  React.useEffect(() => {
    const items = gmailStatus?.connections || [];
    if (!items.length) {
      if (gmailCredentialId) setGmailCredentialId('');
      return;
    }
    if (!gmailCredentialId || !items.some((c) => c.id === gmailCredentialId)) {
      setGmailCredentialId(items[0].id);
    }
  }, [gmailStatus, gmailCredentialId]);

  const { data: runsData, refetch: refetchRuns, isFetching: runsFetching } = useQuery({
    queryKey: ['identities', 'reddit-standalone-runs'],
    queryFn: () => api.get('/identities/reddit-standalone/runs', { params: { limit: 20 } }).then(r => r.data),
    enabled: platformFilter === 'reddit' && view === 'list',
    refetchInterval: (query) => {
      const items = query.state.data?.items || [];
      const live = items.some((r) => r.status === 'queued' || r.status === 'running');
      return live ? 4000 : 15000;
    },
  });

  // Marketplace also needs the agent roster (drag-targets). Keep it lazy
  // so the list view doesn't pay this query when not in marketplace mode.
  const { data: agentsData, isLoading: agentsLoading } = useQuery({
    queryKey: ['agents', 'all'],
    queryFn: () => api.get('/agents', { params: { limit: 100 } }).then(r => r.data),
    enabled: view === 'marketplace',
    staleTime: 30_000,
  });

  const releaseMut = useMutation({
    mutationFn: (id) => api.post(`/identities/${id}/release`).then(r => r.data),
    onSuccess: () => { toast.success('Identity released'); qc.invalidateQueries({ queryKey: ['identities'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Release failed'),
  });
  const archiveMut = useMutation({
    mutationFn: (id) => api.delete(`/identities/${id}`).then(r => r.data),
    onSuccess: () => { toast.success('Identity archived'); qc.invalidateQueries({ queryKey: ['identities'] }); setDetails(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Archive failed'),
  });
  const banMut = useMutation({
    mutationFn: (id) => api.patch(`/identities/${id}`, { status: 'banned' }).then(r => r.data),
    onSuccess: () => { toast.success('Identity marked banned'); qc.invalidateQueries({ queryKey: ['identities'] }); setDetails(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Update failed'),
  });

  const runsRef = React.useRef(null);
  const runs = runsData?.items || [];
  const runSummary = runsData?.summary || {};
  const liveRunCount = (runSummary.queued || 0) + (runSummary.running || 0);
  const failedRunCount = runSummary.failed || 0;

  const connectGmailMut = useMutation({
    mutationFn: () => api.get('/identities/gmail-oauth/start').then(r => r.data),
    onSuccess: (res) => {
      if (res?.auth_url) {
        window.location.href = res.auth_url;
        return;
      }
      toast.error('OAuth start returned no auth_url');
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d?.message || JSON.stringify(d)) : (d || 'Gmail OAuth start failed'));
    },
  });

  const disconnectGmailMut = useMutation({
    mutationFn: (id) => api.delete(`/identities/gmail-oauth/connections/${id}`).then(r => r.data),
    onSuccess: () => {
      toast.success('Gmail disconnected');
      refetchGmail();
      refetchCapacity();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Disconnect failed'),
  });

  const batchMut = useMutation({
    mutationFn: (body) => api.post('/identities/reddit-standalone/batch', body).then(r => r.data),
    onSuccess: (res) => {
      const n = res?.queued || 0;
      const via = res?.verify === 'gmail_oauth' ? 'Gmail alias' : 'mail.tm';
      toast.success(res?.capped_by_proxies
        ? `Queued ${n} Reddit signup(s) via ${via} (capped by free proxies)`
        : `Queued ${n} Reddit signup(s) via ${via}`);
      setMailboxText('');
      qc.invalidateQueries({ queryKey: ['identities'] });
      refetchCapacity();
      refetchRuns();
      requestAnimationFrame(() => {
        runsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d?.message || JSON.stringify(d)) : (d || 'Batch failed'));
    },
  });

  const retryMut = useMutation({
    mutationFn: (taskId) => api.post(`/identities/reddit-standalone/runs/${taskId}/retry`).then(r => r.data),
    onSuccess: (res) => {
      toast.success(`Retry queued for ${res?.agent_name || 'agent'}`);
      refetchRuns();
      refetchCapacity();
      qc.invalidateQueries({ queryKey: ['identities'] });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d?.message || JSON.stringify(d)) : (d || 'Retry failed'));
    },
  });

  const freeProxies = capacity?.free_proxies ?? 0;
  const maxAccounts = capacity?.max_accounts ?? 0;
  const mailboxes = React.useMemo(() => parseMailboxLines(mailboxText), [mailboxText]);

  const gmailConnections = gmailStatus?.connections || [];
  const gmailConfigured = Boolean(gmailStatus?.configured);
  const selectedGmail = gmailConnections.find((c) => c.id === gmailCredentialId) || null;

  const launchBatch = (mode) => {
    const count = Math.max(1, Math.min(Number(batchCount) || 1, 50));
    if (maxAccounts <= 0) { toast.error('No free proxies available'); return; }
    if (mode === 'gmail_oauth') {
      if (!selectedGmail) {
        toast.error('Connect a Gmail account first');
        return;
      }
      batchMut.mutate({
        count,
        prefer_mobile_proxy: preferMobile,
        mailboxes: [],
        name_prefix: 'reddit-gmail',
        gmail_credential_id: selectedGmail.id,
        alias_mode: aliasMode,
        email_provider: 'gmail_oauth',
      });
      return;
    }
    // mail.tm — same Reddit flow, disposable inbox for OTP
    batchMut.mutate({
      count,
      prefer_mobile_proxy: preferMobile,
      mailboxes: [],
      name_prefix: 'reddit-mail',
      gmail_credential_id: null,
      email_provider: 'mail_tm',
    });
  };

  const items = data?.items || [];
  const byStatus = data?.by_status || {};

  return (
    <div className="space-y-6" data-testid="identities-page">
      <PageHeader
        title="Account Identity Vault"
        description="Google + Reddit identities minted on the mobile farm. Each row carries cookies + a matching device fingerprint in R2 — claim one to warm/promote on the cheap browser farm."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-white/10 bg-[hsl(var(--surface-2))] p-0.5"
                 data-testid="identities-view-toggle">
              <button
                onClick={() => setView('list')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition ${
                  view === 'list'
                    ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--text-1))]'
                    : 'text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))]'
                }`}
                data-testid="identities-view-list"
              >
                <List className="h-3 w-3" /> List
              </button>
              <button
                onClick={() => setView('marketplace')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition ${
                  view === 'marketplace'
                    ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--text-1))]'
                    : 'text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))]'
                }`}
                data-testid="identities-view-marketplace"
              >
                <LayoutGrid className="h-3 w-3" /> Marketplace
              </button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => refetch()}
                     disabled={isFetching} data-testid="identities-refresh">
              {isFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
              Refresh
            </Button>
          </div>
        }
      />

      {view === 'marketplace' ? (
        <IdentityMarketplace
          identities={items}
          agents={agentsData?.items || agentsData || []}
          isLoading={isLoading || agentsLoading}
        />
      ) : (
        <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {['fresh', 'in_use', 'banned', 'archived'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter((cur) => cur === s ? 'all' : s)}
            className={`rounded-md border p-3 text-left transition ${
              statusFilter === s
                ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                : 'border-white/10 bg-[hsl(var(--surface-2))] hover:border-white/20'
            }`}
            data-testid={`identities-stat-${s}`}
          >
            <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">{s.replace('_', ' ')}</div>
            <div className="mt-1 text-2xl font-semibold text-[hsl(var(--text-1))] tabular-nums">
              {byStatus[s] ?? 0}
            </div>
          </button>
        ))}
      </div>

      <Tabs value={platformFilter} onValueChange={setPlatformFilter}>
        <TabsList data-testid="identities-platform-tabs">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="google" data-testid="tab-google">Google</TabsTrigger>
          <TabsTrigger value="reddit_via_google" data-testid="tab-reddit-google">Reddit (linked)</TabsTrigger>
          <TabsTrigger value="reddit" data-testid="tab-reddit">Reddit (standalone)</TabsTrigger>
        </TabsList>
      </Tabs>

      {platformFilter === 'reddit' && (
        <div
          className="rounded-md border border-white/10 bg-[hsl(var(--surface-2))] p-4 space-y-3"
          data-testid="reddit-standalone-factory"
        >
                    <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-[hsl(var(--text-1))]">
                Create Reddit accounts
              </div>
              <p className="mt-1 max-w-2xl text-[11px] text-[hsl(var(--text-3))]">
                Two options: disposable mail.tm inbox, or a Gmail +/. alias on the connected
                OAuth account (OTP read via Gmail API). Same Reddit signup either way.
              </p>
            </div>
            <div className="text-right text-[11px] text-[hsl(var(--text-2))]" data-testid="reddit-standalone-capacity">
              <div>
                Free proxies:{' '}
                <span className="font-mono text-[hsl(var(--text-1))] tabular-nums">{freeProxies}</span>
                {typeof capacity?.free_mobile_proxies === 'number' && (
                  <span className="opacity-70"> ({capacity.free_mobile_proxies} mobile)</span>
                )}
              </div>
              <div className="opacity-70">
                Gmail connected: {capacity?.gmail_oauth_connections ?? gmailConnections.length}
              </div>
              <div className="opacity-70">Max this run: {maxAccounts}</div>
            </div>
          </div>

          <div
            className="rounded border border-white/10 bg-[hsl(var(--surface-1))] p-3 space-y-2"
            data-testid="gmail-oauth-panel"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] text-[hsl(var(--text-2))]">
                {gmailConfigured ? (
                  selectedGmail ? (
                    <>Using <span className="font-mono text-[hsl(var(--text-1))]">{selectedGmail.email}</span> for aliases</>
                  ) : (
                    <>OAuth ready — connect a Gmail to mint +/. aliases</>
                  )
                ) : (
                  <>Server missing GOOGLE_OAUTH_CLIENT_ID / SECRET — mail.tm fallback only</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => connectGmailMut.mutate()}
                  disabled={!gmailConfigured || connectGmailMut.isPending}
                  data-testid="gmail-oauth-connect"
                >
                  {connectGmailMut.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Globe className="mr-1 h-3.5 w-3.5" />
                  )}
                  {gmailConnections.length ? 'Reconnect Gmail' : 'Connect Gmail'}
                </Button>
                {selectedGmail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectGmailMut.mutate(selectedGmail.id)}
                    disabled={disconnectGmailMut.isPending}
                    data-testid="gmail-oauth-disconnect"
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </div>
            {gmailConnections.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">Gmail account</span>
                  <select
                    className="w-full rounded-md border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-2 text-[12px] text-[hsl(var(--text-1))]"
                    value={gmailCredentialId}
                    onChange={(e) => setGmailCredentialId(e.target.value)}
                    data-testid="gmail-oauth-select"
                  >
                    {gmailConnections.map((c) => (
                      <option key={c.id} value={c.id}>{c.email}</option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">Alias mode</span>
                  <select
                    className="w-full rounded-md border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-2 text-[12px] text-[hsl(var(--text-1))]"
                    value={aliasMode}
                    onChange={(e) => setAliasMode(e.target.value)}
                    data-testid="gmail-alias-mode"
                  >
                    <option value="plus_dot">Plus + dots (recommended)</option>
                    <option value="plus">Plus tags only</option>
                    <option value="dot">Dots + unique tag</option>
                  </select>
                </label>
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-[140px_1fr] sm:items-end">
            <label className="block space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">Accounts</span>
              <Input
                type="number"
                min={1}
                max={Math.max(1, maxAccounts || 50)}
                value={batchCount}
                onChange={(e) => setBatchCount(e.target.value)}
                data-testid="reddit-standalone-count"
              />
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] text-[hsl(var(--text-2))]">
                <input
                  type="checkbox"
                  checked={preferMobile}
                  onChange={(e) => setPreferMobile(e.target.checked)}
                  data-testid="reddit-standalone-prefer-mobile"
                />
                Prefer mobile proxies
              </label>
              <p className="text-[11px] text-[hsl(var(--text-3))]" data-testid="reddit-standalone-flow-note">
                Same Reddit signup for both. mail.tm uses a disposable inbox; Gmail uses a +/. alias on the connected account and reads the OTP via Gmail API.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => launchBatch('mail_tm')}
                  disabled={batchMut.isPending || maxAccounts <= 0}
                  data-testid="reddit-standalone-launch-mail-tm"
                >
                  {batchMut.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-3.5 w-3.5" />
                  )}
                  Create with mail.tm ({Math.min(Number(batchCount) || 1, maxAccounts || 1)})
                </Button>
                <Button
                  onClick={() => launchBatch('gmail_oauth')}
                  disabled={batchMut.isPending || maxAccounts <= 0 || !selectedGmail}
                  data-testid="reddit-standalone-launch"
                >
                  {batchMut.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-3.5 w-3.5" />
                  )}
                  Create with Gmail alias ({Math.min(Number(batchCount) || 1, maxAccounts || 1)})
                </Button>
              </div>
              {/* Hidden legacy mailbox field kept for tests */}
              <textarea
                value={mailboxText}
                onChange={(e) => setMailboxText(e.target.value)}
                rows={1}
                className="hidden"
                data-testid="reddit-standalone-mailboxes"
                aria-hidden="true"
              />
            </div>
          </div>

          {(liveRunCount > 0 || failedRunCount > 0) && (
            <div
              className={`rounded border px-3 py-2 text-[11px] ${
                liveRunCount > 0
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                  : 'border-rose-500/40 bg-rose-500/10 text-rose-100'
              }`}
              data-testid="reddit-standalone-live-banner"
            >
              {liveRunCount > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {liveRunCount} signup{liveRunCount === 1 ? '' : 's'} in progress
                  {failedRunCount > 0 ? ` · ${failedRunCount} failed` : ''}
                  — status updates every few seconds below (also under Tasks).
                </span>
              ) : (
                <span>
                  Last batch: {failedRunCount} failed. Use Retry on each row below, or Create again
                  (new agents). Vault rows only appear after a successful signup.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {platformFilter === 'reddit' && (
        <div
          ref={runsRef}
          className={`rounded-md border overflow-hidden ${
            liveRunCount > 0
              ? 'border-amber-500/40 bg-[hsl(var(--surface-1))]'
              : failedRunCount > 0
                ? 'border-rose-500/30 bg-[hsl(var(--surface-1))]'
                : 'border-white/10 bg-[hsl(var(--surface-1))]'
          }`}
          data-testid="reddit-standalone-runs"
        >
          <div className="flex items-center justify-between gap-2 border-b border-white/10 bg-[hsl(var(--surface-2))] px-3 py-2">
            <div className="text-[11px] font-medium text-[hsl(var(--text-1))]">
              Factory progress
              {runsData?.summary && (
                <span className="ml-2 font-normal text-[hsl(var(--text-3))]">
                  queued {runsData.summary.queued || 0} · running {runsData.summary.running || 0} ·
                  ok {runsData.summary.completed || 0} · failed {runsData.summary.failed || 0}
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                    onClick={() => refetchRuns()} disabled={runsFetching}
                    data-testid="reddit-standalone-runs-refresh">
              {runsFetching ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
          {!runs.length ? (
            <div className="p-4 text-[11px] text-[hsl(var(--text-3))]">
              No factory jobs yet. After you click Create, each account appears here as queued →
              running → completed/failed (vault table below only lists finished identities).
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {runs.map((run) => {
                const live = run.status === 'queued' || run.status === 'running';
                const failed = run.status === 'failed';
                const ok = run.status === 'completed';
                const tone = ok
                  ? 'text-emerald-300'
                  : failed
                    ? 'text-rose-300'
                    : live
                      ? 'text-amber-300'
                      : 'text-[hsl(var(--text-2))]';
                return (
                  <div key={run.task_id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2"
                       data-testid={`reddit-standalone-run-${run.task_id}`}>
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={`font-medium ${tone}`}>
                          {live && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
                          {run.status}{run.verdict ? ` · ${run.verdict}` : ''}
                        </span>
                        <span className="font-mono text-[hsl(var(--text-3))]">{run.agent_name || run.agent_id?.slice(0, 8)}</span>
                        {run.email && <span className="font-mono text-[hsl(var(--text-2))]">{run.email}</span>}
                        {run.create_tuta && !run.email && (
                          <span className="text-[hsl(var(--text-3))]">auto-Tuta</span>
                        )}
                      </div>
                      {run.reason && (
                        <div className="max-w-3xl text-[10px] text-rose-300/90 break-words">{run.reason}</div>
                      )}
                      <div className="text-[10px] text-[hsl(var(--text-3))]">
                        {run.created_at ? relativeTime(run.created_at) : ''}
                        {run.agent_status ? ` · agent ${run.agent_status}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {failed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-amber-200"
                          disabled={retryMut.isPending}
                          onClick={() => retryMut.mutate(run.task_id)}
                          data-testid={`reddit-standalone-retry-${run.task_id}`}
                        >
                          {retryMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
                        </Button>
                      )}
                      <a
                        href={`/tasks/${run.task_id}`}
                        className="text-[10px] text-[hsl(var(--primary))] hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`reddit-standalone-run-link-${run.task_id}`}
                      >
                        Open task
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border border-white/10 bg-[hsl(var(--surface-1))] overflow-hidden">
        <table className="w-full text-sm" data-testid="identities-table">
          <thead className="bg-[hsl(var(--surface-2))] text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">
            <tr>
              <th className="px-3 py-2 text-left">Platform</th>
              <th className="px-3 py-2 text-left">Identity</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Pad / Fingerprint</th>
              <th className="px-3 py-2 text-left">Linked</th>
              <th className="px-3 py-2 text-left">Last used</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && (
              <tr><td colSpan={7} className="p-8 text-center text-xs text-[hsl(var(--text-3))]">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading vault…
              </td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-xs text-[hsl(var(--text-3))]">
                <KeyRound className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {platformFilter === 'reddit' ? (
                  liveRunCount > 0 ? (
                    <>Signups in progress — watch <span className="text-amber-200">Factory progress</span> above. Finished accounts land here.</>
                  ) : failedRunCount > 0 ? (
                    <>No vault rows yet. {failedRunCount} factory job{failedRunCount === 1 ? '' : 's'} failed — see Factory progress above (Retry or Open task).</>
                  ) : (
                    <>No Reddit standalone identities yet. Use the factory above to mint unverified Reddit accounts.</>
                  )
                ) : (
                  <>No identities yet. Submit a <span className="font-mono">mobile_signup_google</span> task to mint one.</>
                )}
              </td></tr>
            )}
            {items.map((row) => {
              const Icon = PLATFORM_ICONS[row.platform] || KeyRound;
              return (
                <tr
                  key={row.id}
                  className="hover:bg-[hsl(var(--surface-2))]/50 cursor-pointer"
                  onClick={() => setDetails(row)}
                  data-testid={`identity-row-${row.id}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-[hsl(var(--text-3))]" />
                      <span className="text-[11px] text-[hsl(var(--text-2))]">{row.platform}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[hsl(var(--text-1))]">
                    {row.email || row.username || row.id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${STATUS_COLORS[row.status] || ''}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[hsl(var(--text-3))]">
                    <div>{row.created_via_pad_code || '—'}</div>
                    <div className="opacity-60">{row.fingerprint_hash?.slice(0, 12) || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[hsl(var(--text-3))]">
                    {row.linked_identity_id ? row.linked_identity_id.slice(0, 8) : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-[hsl(var(--text-3))]">
                    <div>{row.last_used_at ? relativeTime(row.last_used_at) : 'never'}</div>
                    <div className="opacity-60">{row.last_used_runtime || ''}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      disabled={row.status !== 'in_use'}
                      onClick={(e) => { e.stopPropagation(); releaseMut.mutate(row.id); }}
                      data-testid={`release-${row.id}`}
                    >
                      Release
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        </>
      )}

      <Dialog open={!!details} onOpenChange={(o) => { if (!o) setDetails(null); }}>
        <DialogContent className="max-w-2xl" data-testid="identity-detail-dialog">
          {details && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-base">
                  {details.email || details.username || details.id}
                </DialogTitle>
                <DialogDescription className="text-[11px]">
                  Platform: {details.platform} · Status: {details.status} · Created {relativeTime(details.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-[12px]">
                <Row label="Identity ID" value={details.id} mono />
                <Row label="Email" value={details.email || '—'} mono />
                <Row label="Username" value={details.username || '—'} mono />
                <Row label="Recovery email" value={details.recovery_email || '—'} mono />
                <Row label="Recovery phone" value={details.recovery_phone || '—'} mono />
                <Row label="Egress IP (device)" value={details.meta?.egress_ip || details.bundle?.egress_ip || '—'} mono />
                <Row label="Geo" value={
                  (() => {
                    const g = details.meta?.geo || details.bundle?.geo || {};
                    const parts = [g.city, g.regionName, g.country, g.isp].filter(Boolean);
                    return parts.length ? parts.join(' · ') : '—';
                  })()
                } />
                <Row label="Device persona country" value={details.meta?.device_country_iso || '—'} />
                <Row label="SMS rented from" value={details.meta?.rented_country || details.meta?.sms_country_pref || '—'} />
                <Row label="R2 key" value={details.r2_key} mono />
                <Row label="Created via pad" value={details.created_via_pad_code || '—'} mono />
                <Row label="Fingerprint hash" value={details.fingerprint_hash || '—'} mono />
                <Row label="Linked identity" value={details.linked_identity_id || '—'} mono />
                <Row label="Cookies captured"
                     value={typeof details.meta?.cookies_count === 'number'
                       ? `${details.meta.cookies_count} cookies`
                       : (details.cookies?.length ? `${details.cookies.length} cookies (loaded)` : '—')} />
                <Row label="Session summary" value={Array.isArray(details.session_cookies) ? `${details.session_cookies.length} entries` : '—'} />
                <Row label="TOTP" value={details.has_totp ? 'configured' : '—'} />
                <Row label="Backup codes" value={typeof details.backup_codes_count === 'number' ? `${details.backup_codes_count}` : '—'} />
                <Row label="Created" value={details.created_at ? `${new Date(details.created_at).toLocaleString()} (${relativeTime(details.created_at)})` : '—'} />
                <Row label="Last used" value={details.last_used_at
                  ? `${relativeTime(details.last_used_at)} (${details.last_used_runtime})`
                  : 'never'} />
                {/* Phase Q.2.5e — password reveal. Decrypts on demand
                    via /identities/{id}/reveal so we don't ship the
                    plaintext on every list-page load. */}
                {details.password ? (
                  <Row label="Password" value={details.password} mono />
                ) : (
                  <div className="flex items-center justify-between rounded border border-amber-500/30 bg-amber-500/10 p-2"
                        data-testid="identity-reveal-row">
                    <span className="text-[11px] text-[hsl(var(--text-3))]">
                      Password is encrypted at rest. Click to decrypt + load cookies.
                    </span>
                    <Button
                      variant="outline" size="sm"
                      data-testid="identity-reveal-button"
                      onClick={async () => {
                        try {
                          const r = await api.get(`/identities/${details.id}/reveal`);
                          setDetails((d) => ({ ...d, ...r.data }));
                        } catch (e) {
                          toast.error('Reveal failed: ' + (e?.response?.data?.detail || e.message));
                        }
                      }}
                    >
                      <KeyRound className="mr-1 h-3 w-3" /> Reveal
                    </Button>
                  </div>
                )}
                {details.totp_secret && (
                  <Row label="TOTP secret" value={details.totp_secret} mono />
                )}
                {Array.isArray(details.backup_codes) && details.backup_codes.length > 0 && (
                  <details className="rounded border border-white/10 bg-[hsl(var(--surface-2))] p-2"
                            data-testid="identity-backup-codes-block">
                    <summary className="cursor-pointer text-[11px] text-[hsl(var(--text-3))]">
                      Backup codes ({details.backup_codes.length})
                    </summary>
                    <pre className="mt-2 max-h-40 overflow-auto text-[10px] text-[hsl(var(--text-2))]">
                      {details.backup_codes.join('\n')}
                    </pre>
                  </details>
                )}
                {details.meta?.cookie_capture && (
                  <Row label="Cookie capture" value={details.meta.cookie_capture} />
                )}
                {details.meta && Object.keys(details.meta).length > 0 && (
                  <details className="rounded border border-white/10 bg-[hsl(var(--surface-2))] p-2">
                    <summary className="cursor-pointer text-[11px] text-[hsl(var(--text-3))]">Raw meta</summary>
                    <pre className="mt-2 overflow-x-auto text-[10px] text-[hsl(var(--text-2))]">
                      {JSON.stringify(details.meta, null, 2)}
                    </pre>
                  </details>
                )}
                {Array.isArray(details.cookies) && details.cookies.length > 0 && (
                  <details className="rounded border border-white/10 bg-[hsl(var(--surface-2))] p-2"
                            data-testid="identity-cookies-block">
                    <summary className="cursor-pointer text-[11px] text-[hsl(var(--text-3))]">
                      Cookies ({details.cookies.length})
                    </summary>
                    <pre className="mt-2 max-h-72 overflow-auto text-[10px] text-[hsl(var(--text-2))]">
                      {JSON.stringify(details.cookies, null, 2)}
                    </pre>
                  </details>
                )}
                {Array.isArray(details.session_cookies) && details.session_cookies.length > 0 && (
                  <details className="rounded border border-white/10 bg-[hsl(var(--surface-2))] p-2"
                            data-testid="identity-session-cookies-block">
                    <summary className="cursor-pointer text-[11px] text-[hsl(var(--text-3))]">
                      Session summary ({details.session_cookies.length})
                    </summary>
                    <pre className="mt-2 max-h-72 overflow-auto text-[10px] text-[hsl(var(--text-2))]">
                      {JSON.stringify(details.session_cookies, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-white/10 pt-3">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => banMut.mutate(details.id)}
                  disabled={details.status === 'banned' || banMut.isPending}
                  data-testid="identity-ban"
                >
                  <ShieldAlert className="mr-1 h-3 w-3" /> Mark banned
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => archiveMut.mutate(details.id)}
                  disabled={details.status === 'archived' || archiveMut.isPending}
                  data-testid="identity-archive"
                >
                  <Archive className="mr-1 h-3 w-3" /> Archive
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-white/5 pb-1">
      <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-3))]">{label}</span>
      <span className={`text-[11px] text-[hsl(var(--text-1))] ${mono ? 'font-mono' : ''} text-right break-all`}>
        {value}
      </span>
    </div>
  );
}
